import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonItem,
  IonLoading,
  useIonViewDidEnter,
} from '@ionic/react';
import { refresh } from 'ionicons/icons';
import { PageLayout } from 'containers/layout';
import TicketUsers from 'containers/tab/TicketUsers';
import { LocationDetail, MenuItemList } from 'components/tab';
import AppActions, { AppSelectors } from 'redux/AppRedux';
import PayActions, { PaySelectors } from 'redux/PayRedux';
import { AuthSelectors } from 'redux/AuthRedux';
import PromotionSlides from 'containers/promotions/PromotionSlides';
import LoyaltyJoinModal from 'containers/tab/LoyaltyJoinModal';
import LoyaltyJoinConfirmModal from 'containers/tab/LoyaltyJoinConfirmModal';
import LoyaltyInfo from 'containers/tab/LoyaltyInfo';
import LoyaltyRedeemModal from 'containers/tab/LoyaltyRedeemModal';
import LoyaltyStarModal from 'containers/tab/LoyaltyStarModal';
import LoyaltyMenuItemModal from 'containers/tab/LoyaltyMenuItemModal';
import BleScanRestartModal from 'containers/tab/BleScanRestartModal';
import BleScanTipModal from 'containers/tab/BleScanTipModal';
import { WaveIdManager } from 'sdk/WakeIdManager';
import storage from 'utils/storage';
import {
  LOYALTY_JOIN_CONFIRM,
  LOYALTY_JOIN,
  LOYALTY_REDEEM,
  LOYALTY_STAR,
  LOYALTY_MENU_ITEM,
  BLE_SCAN_RESTART,
  BLE_SCAN_TIP,
} from './Loyalty';

const ConstantVal = {
  DEFAULT_RSSI_LEVEL: '-50',
  DEFAULT_CARD_ID: '98765',
  DEVICE_NAME: 'RFIDeas',
  UUID_RF_IDEAS_SERVICE_ID: '0000A800-0000-1000-8000-00805F9B0C27',
  WRITE_CHARACTERISTIC_UUID: '0000AA00-0000-1000-8000-00805F9B0C27',
  READ_CHARACTERISTIC_UUID: '0000AC00-0000-1000-8000-00805F9B0C27',
  MANUFACTURER_DATA: {
    android: [
      2, 1, 6, 17, 7, 39, 12, 155, 95, 128, 0, 0, 128, 0, 16, 0, 0, 0, 168, 0,
      0, 2, 10, 0, 21, 255, 2, 255, 0, 17, 34, 51, 68, 85, 102, 119, 136, 153,
      170, 187, 204, 221, 238, 255, 0, 1, 8, 9, 82, 70, 73, 68, 101, 97, 115, 0,
      0, 0, 0, 0, 0, 0,
    ],
    ios: [
      2, 255, 0, 17, 34, 51, 68, 85, 102, 119, 136, 153, 170, 187, 204, 221,
      238, 255, 0, 1,
    ],
  },
};

const MAX_SCAN_TIME = 10;

function ViewTab() {
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const user = useSelector(AuthSelectors.selectUser);
  const venue = useSelector(AppSelectors.selectVenue);
  const venueDetail = useSelector(AppSelectors.selectVenueDetail);
  const loadingTicket = useSelector(PaySelectors.selectLoadingTicket);
  const ticket = useSelector(PaySelectors.selectTicket);
  const paymentMethods = useSelector(PaySelectors.selectPaymentMethods);
  const isTable = useSelector(PaySelectors.selectIsTable);
  const userCount = useSelector(PaySelectors.selectTicketUserCount);
  const isPaid = useSelector(PaySelectors.selectIsPaid);
  const ticketItems = useSelector(PaySelectors.selectItems);
  const payType = useSelector(PaySelectors.selectPayType);
  const myPrice = useSelector(PaySelectors.selectMyPrice);

  const loyaltyPrograms = useSelector(PaySelectors.selectLoyaltyPrograms);
  const loyaltyPoints = useSelector(PaySelectors.selectLoyaltyPoints);
  const rewardLevels = useSelector(PaySelectors.selectRewardLevels);
  const loyaltyPopupTypeModal = useSelector(
    PaySelectors.selectLoyaltyTypeModal
  );

  const [rewardLevel, setRewardLevel] = useState(null);

  let BleManager;

  if (!BleManager) {
    BleManager = new WaveIdManager(
      ConstantVal.DEVICE_NAME,
      ConstantVal.UUID_RF_IDEAS_SERVICE_ID,
      ConstantVal.WRITE_CHARACTERISTIC_UUID,
      ConstantVal.MANUFACTURER_DATA,
      ConstantVal.READ_CHARACTERISTIC_UUID,
      null
    );
  }

  /**
   *use for `bluetooth` component initialization
   */
  const init = async () => {
    try {
      await BleManager.checkPermission();
      await BleManager.enableBluetooth();
      await BleManager.enableLocation();
      await BleManager.init();
    } catch (e) {
      console.log(e);
    }
  };

  const setLoyaltyTypeModal = (currentTypeModal) => {
    dispatch(PayActions.setLoyaltyTypeModal(currentTypeModal));
  };

  useEffect(() => {
    init();

    return () => {
      if (BleManager) {
        BleManager.destroy();
      }
    };
  }, []);

  const checkBLEScanTipModal = useCallback(async () => {
    if (venueDetail) {
      const isFirstLoading = await storage.getItem('PREP_CAN_BLE_TIP');

      if (
        venueDetail.mobileCredentialEnabled &&
        isFirstLoading &&
        !isFirstLoading.value
      ) {
        setLoyaltyTypeModal(BLE_SCAN_TIP);
      }
    }
  }, [venueDetail]);


  const checkLoyaltyJoinModal = async () => {

    if (ticket) {
      const isFirstLoading = await storage.getItem('PREP_LOYALTY_JOIN_MODAL');
      
      dispatch(PayActions.loadRewardLevels());
      if (isFirstLoading && !isFirstLoading.value) {
        dispatch(PayActions.loadLoyaltyPoints(true));
      } else {
        dispatch(PayActions.loadLoyaltyPoints());
      }
    }
  };

  useEffect(() => {
    checkBLEScanTipModal();
  }, []);

  useEffect(() => {
    checkLoyaltyJoinModal();
  }, [ticket]);

  const userMobileCredential = useMemo(() => {
    if (user.mobileCredential) {
      return user.mobileCredential;
    }

    return ConstantVal.DEFAULT_CARD_ID;
  }, [user]);

  const handlePay = () => {
    dispatch(PayActions.loadTicket());
    history.push(`/locations/${venue}/pay`);
  };

  useIonViewDidEnter(async () => {
    if (paymentMethods.length) {
      dispatch(AppActions.setVenue(params.locationId));
      dispatch(PayActions.loadTicket(true));
    } else {
      dispatch(AppActions.setToast('Please add payment method', 'danger'));
      history.replace(`/payment-methods`, { direction: `forward` });
    }
  }, [params.locationId]);

  const refreshAction = () => {
    dispatch(PayActions.loadTicket(true));
  };

  const programId = useMemo(() => {
    const filterLoyaltyPrograms = loyaltyPrograms?.filter((item) =>
      item?.venueIds?.some((id) => id === venueDetail?.id)
    );

    if (filterLoyaltyPrograms.length > 0) {
      return filterLoyaltyPrograms[0].id;
    }

    return null;
  }, [loyaltyPrograms, venueDetail]);

  const venueLoyaltyPoints = useMemo(() => {
    const filterLoyaltyPoints = loyaltyPoints.filter(
      (item) => item.programId === programId
    );

    if (filterLoyaltyPoints.length > 0) {
      return filterLoyaltyPoints[0];
    }

    return null;
  }, [loyaltyPoints, programId]);

  /**
   *its authenticates the device
   * @param {*} peripheral
   */
  const authDevice = async (peripheral) => {
    try {
      AppActions.setToast('Device found', 'danger');
      const auth = await BleManager.authPeripheral(
        peripheral,
        userMobileCredential
      );

      AppActions.setToast(
        auth?.success ? 'Authenticated.' : 'Authentication Failed.',
        'danger'
      );
      await new Promise((resolve1) => setTimeout(resolve1, 1000));

      setLoyaltyTypeModal(null);
    } catch (e) {
      AppActions.setToast(
        e || 'Something went wrong. Please try again.',
        'danger'
      );
      await new Promise((resolve1) => setTimeout(resolve1, 1000));
    }
  };

  const startScanNew = async () => {
    setLoyaltyTypeModal(BLE_SCAN_RESTART);

    const data = await BleManager.startScan(
      MAX_SCAN_TIME,
      ConstantVal.DEFAULT_RSSI_LEVEL
    );

    if (data?.flag) {
      await authDevice(data.peripheral);
    } else {
      dispatch(AppActions.setToast('Device not found.', 'danger'));

      await new Promise((resolve1) => setTimeout(resolve1, 1000));
    }
  };

  const onOrderMoreItemsAction = () => {
    history.push(`/locations/${venue}/cart/add`);
  };

  const showOrderMoreItems = () => {
    if (
      (venueDetail &&
        venueDetail.orderAtTableEnabled &&
        ticket &&
        ticket.table) ||
      (venueDetail &&
        venueDetail.orderAtLocationEnabled &&
        ticket &&
        !ticket.table)
    )
      return true;
    return false;
  };

  return (
    <PageLayout
      title={!ticket ? '' : ticket.is_pickup ? 'Review Order' : 'View Tab'}
      back="/locations"
      header={
        <IonButtons slot="end">
          <IonButton
            onClick={() => {
              refreshAction();
            }}
          >
            <IonIcon slot="icon-only" icon={refresh} />
          </IonButton>
        </IonButtons>
      }
      footer={
        <IonButton
          expand="full"
          fill="solid"
          color="tertiary"
          onClick={handlePay}
          disabled={(isPaid && payType === 'portion') || myPrice === 0}
          className="layout-margin-inset-bottom"
        >
          {!ticket
            ? 'Loading...'
            : ticket.is_pickup
              ? 'Pay'
              : isPaid && payType === 'portion'
                ? 'Already Paid'
                : payType === 'item'
                  ? 'Pay My Portion'
                  : userCount > 1 && !venueDetail.isFullPay
                    ? 'Split Tab Evenly'
                    : 'Pay Tab'}
        </IonButton>
      }
    >
      <IonLoading isOpen={loadingTicket} message="Loading..." />

      <LoyaltyInfo
        programId={programId}
        venueDetail={venueDetail}
        loyaltyPoints={venueLoyaltyPoints}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        startScanNew={startScanNew}
      />

      <PromotionSlides venueId={venue} />

      <LocationDetail ticket={ticket}>
        <TicketUsers venueId={venue} ticket={ticket} />
      </LocationDetail>

      <LoyaltyJoinModal
        isOpen={loyaltyPopupTypeModal === LOYALTY_JOIN}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        programId={programId}
        venueDetail={venueDetail}
        rewardLevels={rewardLevels}
      />

      <LoyaltyJoinConfirmModal
        isOpen={loyaltyPopupTypeModal === LOYALTY_JOIN_CONFIRM}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        loyaltyPoints={venueLoyaltyPoints}
        venueDetail={venueDetail}
        refreshAction={refreshAction}
        locationId={params.locationId}
      />

      <LoyaltyStarModal
        isOpen={loyaltyPopupTypeModal === LOYALTY_STAR}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        venueDetail={venueDetail}
        rewardLevels={rewardLevels}
        loyaltyPoints={venueLoyaltyPoints}
      />

      <LoyaltyRedeemModal
        isOpen={loyaltyPopupTypeModal === LOYALTY_REDEEM}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        loyaltyPoints={venueLoyaltyPoints}
        venueId={venue}
        ticket={ticket}
        rewardLevel={rewardLevel}
        setRewardLevel={setRewardLevel}
        rewardLevels={rewardLevels}
      />

      <LoyaltyMenuItemModal
        isOpen={loyaltyPopupTypeModal === LOYALTY_MENU_ITEM}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        loyaltyPoints={venueLoyaltyPoints}
        venueId={venue}
        rewardLevel={rewardLevel}
        ticket={ticket}
        ticketItems={ticketItems}
      />

      {showOrderMoreItems() && (
        <IonItem lines="none">
          <IonButton
            color="secondary"
            expand="block"
            fill="solid"
            type="button"
            className="order-more-items-btn"
            onClick={onOrderMoreItemsAction}
          >
            Place Mobile Order
          </IonButton>
        </IonItem>
      )}

      <MenuItemList
        venue={venueDetail}
        isTable={isTable}
        ticket={ticket}
        myPrice={myPrice}
        items={ticketItems}
        payType={payType}
      />

      <BleScanTipModal
        isOpen={loyaltyPopupTypeModal === BLE_SCAN_TIP}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
      />

      <BleScanRestartModal
        isOpen={loyaltyPopupTypeModal === BLE_SCAN_RESTART}
        setLoyaltyTypeModal={setLoyaltyTypeModal}
        restartAction={startScanNew}
      />
    </PageLayout>
  );
}

export default ViewTab;
